'use client';

import React, { useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import CustomerApiService from '@/services/CustomerApiService';
import { useCustomerPanelUrlBuilder, useCustomerPanelTranslations } from '@/lib/customer-panel-utils';
import { extractErrorMessage } from '@/lib/customer-panel/error-handler';
import { ErrorMessage, FormField, LoadingSpinner } from '@/lib/customer-panel/ui-components';
import { createFieldAriaAttributes } from '@/lib/customer-panel/accessibility';
import type { PageUrlMap } from '@/types/blog';

interface DeleteAccountFormProps {
  language?: string;
  translations?: Record<string, string>;
  pageUrls?: PageUrlMap;
  defaultLanguage?: string;
}

export default function DeleteAccountForm({
  language = 'en',
  translations = {},
  pageUrls,
  defaultLanguage = 'en',
}: DeleteAccountFormProps) {
  const t = useCustomerPanelTranslations(translations);
  const buildUrl = useCustomerPanelUrlBuilder(pageUrls, language, defaultLanguage);
  const { logout } = useCustomerAuth();
  const router = useRouter();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleDelete = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!showConfirm) {
      setShowConfirm(true);
      return;
    }

    if (!password) {
      setError(t('customer_panel.profile.current_password', 'Current Password') + ' ' + t('customer_panel.common.required', 'Required'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await CustomerApiService.deleteAccount(password);
      await logout();
      const homeUrl = buildUrl('index');
      if (homeUrl !== '#') {
        router.push(homeUrl);
      } else {
        router.push('/');
      }
    } catch (err) {
      setError(extractErrorMessage(err, t('customer_panel.common.error', 'An error occurred')));
      setLoading(false);
    }
  }, [showConfirm, password, buildUrl, router, logout, t]);

  const inputBaseClasses = "w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50 disabled:cursor-not-allowed";

  if (!showConfirm) {
    return (
      <button type="button"
        onClick={() => setShowConfirm(true)}
        className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        aria-label={t('customer_panel.settings.delete_account', 'Delete Account')}
      >
        {t('customer_panel.settings.delete_account', 'Delete Account')}
      </button>
    );
  }

  return (
    <form onSubmit={handleDelete} className="space-y-4" noValidate>
      {error && <ErrorMessage message={error} fieldId="delete_password" />}

      <FormField
        id="delete_password"
        label={t('customer_panel.profile.current_password', 'Current Password')}
        required
        error={null}
      >
        <input
          type="password"
          id="delete_password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className={inputBaseClasses}
          autoComplete="current-password"
          {...createFieldAriaAttributes('delete_password', t('customer_panel.profile.current_password', 'Current Password'), true)}
        />
        <p className="mt-1 text-xs text-gray-500" id="delete_password-hint">
          {t('customer_panel.settings.delete_account_warning', 'Are you sure you want to delete your account? This action cannot be undone.')}
        </p>
      </FormField>

      <div className="flex space-x-4">
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={loading ? t('customer_panel.common.loading', 'Loading...') : t('customer_panel.settings.delete_account_confirm', 'Confirm Account Deletion')}
        >
          {loading ? (
            <>
              <LoadingSpinner size="sm" className="inline-block mr-2" />
              {t('customer_panel.common.loading', 'Loading...')}
            </>
          ) : (
            t('customer_panel.settings.delete_account_confirm', 'Confirm Account Deletion')
          )}
        </button>
        <button
          type="button"
          onClick={() => {
            setShowConfirm(false);
            setPassword('');
            setError(null);
          }}
          className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          aria-label={t('customer_panel.common.cancel', 'Cancel')}
        >
          {t('customer_panel.common.cancel', 'Cancel')}
        </button>
      </div>
    </form>
  );
}

